import { apiClient } from './apiClient';
import { ConfigTemplate } from '../types/configTemplate';

/**
 * GET /v2/conf/{id}/detail — the device as the dialog reads it (schema v3):
 * the row (template_id included; the template itself is
 * GET /v2/config-templates/{id}), its own override exactly as stored, and
 * template + override merged (what /v2/conf sends).
 */
export interface DeviceDetail {
  idDevice: string;
  idClient: string | null;
  deviceType: string;
  hardware_model_id?: number | null;
  phone: string;
  address?: string | null;
  postal_code?: string | null;
  conf47Type: string;
  mqtt_voice_dsts: string[] | null;
  is_active?: boolean;
  needsRefresh: boolean;
  date_time: string;
  lastUpdateDateTime: string;
  last_request_at?: string | null;
  last_request_by?: string | null;
  template_id: number | null;
  config_override: Record<string, unknown>;
  effective_config: Record<string, unknown>;
}

/** One row of the top-bar search: enough to list it and to open its dialog. */
export interface DeviceSearchHit {
  idDevice: string;
  idClient: string | null;
  deviceType: string | null;
  phone: string | null;
  needsRefresh: boolean;
}

export interface StoredCustomField {
  name: string;
  value: unknown;
  group?: string;
  notes?: string;
}

/** The custom fields stored in a config body (product, template or override). */
export const customFieldsOf = (config?: Record<string, unknown> | null): StoredCustomField[] => {
  const cf = config?.custom_fields;
  return Array.isArray(cf) ? (cf.filter((c) => c && typeof c === 'object' && 'name' in c) as StoredCustomField[]) : [];
};

/** True when the device has at least one exception to its template. */
export const hasOverride = (detail?: DeviceDetail | null): boolean =>
  Object.keys(detail?.config_override || {}).length > 0;

/** The keys of effective_config the override decides: its plain keys and the names of its custom fields. */
export const overriddenKeysOf = (detail?: DeviceDetail | null): string[] => keysOf(detail?.config_override);

/** The template's custom fields, as stored in its json_config. */
export const templateCustomFieldsOf = (template?: ConfigTemplate | null): StoredCustomField[] =>
  customFieldsOf((template?.json_config || null) as Record<string, unknown> | null);

/** The keys of effective_config a config body decides: its plain keys and the names of its custom fields. */
export const keysOf = (config?: Record<string, unknown> | null): string[] => {
  const body = config || {};
  const keys = Object.keys(body).filter((k) => k !== 'custom_fields');
  return Array.from(new Set([...keys, ...customFieldsOf(body).map((c) => c.name)])).sort();
};

/** The override's custom fields, as stored. */
export const overrideCustomFieldsOf = (detail?: DeviceDetail | null): StoredCustomField[] =>
  customFieldsOf(detail?.config_override);

export interface TemplateForDevice {
  id: number;
  name: string;
  description: string | null;
  device_type: string;
  client_id: string;
  is_default?: boolean;
}

export interface DeviceFirmwareVersion {
  id: number;
  device_id: string;
  component_type: string;  // MAIN, WWWDATA, GSM, MODEM, etc.
  version: string;
  last_check: string | null;
  last_update: string | null;
}

class DeviceService {
  /** The IMEI behind a phone number (E.164 digits), via the registry lookup. */
  /**
   * Devices whose IMEI, phone, type, address or client id contains the
   * text: the list endpoint's own search, a few rows for the top bar.
   */
  async searchDevices(text: string, limit = 8): Promise<DeviceSearchHit[]> {
    const params = new URLSearchParams({ page: '1', page_size: String(limit), full_data: 'true', slim: 'true', search: text });
    const response = await apiClient.get(`/v2/conf?${params.toString()}`);
    const items = Array.isArray(response?.items) ? response.items : [];
    return items.map((row: Record<string, unknown>) => ({
      idDevice: String(row.idDevice ?? ''),
      idClient: (row.idClient as string | null) ?? null,
      deviceType: (row.deviceType as string | null) ?? null,
      phone: (row.phone as string | null) ?? null,
      needsRefresh: Boolean(row.needsRefresh),
    }));
  }

  async findDeviceIdByPhone(phone: string): Promise<string> {
    const response = await apiClient.get(`/v2/registry/lookupphone/${encodeURIComponent(phone)}`);
    return String(response?.idDevice ?? '');
  }

  /**
   * Get detailed device information including template data
   */
  async getDeviceDetail(deviceId: string): Promise<DeviceDetail> {
    return await apiClient.get(`/v2/conf/${deviceId}/detail`);
  }

  /** The template a device follows: GET /v2/config-templates/{id}. */
  async getTemplate(templateId: number): Promise<ConfigTemplate> {
    return await apiClient.get(`/v2/config-templates/${templateId}`);
  }

  /**
   * Patch device fields (phone, client, template…)
   */
  async patchDevice(deviceId: string, data: Record<string, unknown>): Promise<unknown> {
    return await apiClient.patch(`/v2/conf/${deviceId}`, data);
  }

  /**
   * Apply a config template to a device
   */
  async applyTemplate(deviceId: string, templateId: number): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/apply-template`, {
      template_id: templateId
    });
  }

  /**
   * Override specific configuration values for a device
   */
  async overrideConfig(deviceId: string, overrides: Record<string, unknown>): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/override-config`, {
      overrides
    });
  }

  /**
   * Reset device configuration to match the linked template
   */
  async resetToTemplate(deviceId: string): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/reset-to-template`, {});
  }

  /**
   * Get available templates for a device
   */
  async getTemplatesForDevice(deviceId: string): Promise<TemplateForDevice[]> {
    return await apiClient.get(`/v2/conf/${deviceId}/templates`);
  }

  /**
   * Get firmware versions for a device
   */
  async getDeviceFirmwareVersions(deviceId: string): Promise<DeviceFirmwareVersion[]> {
    // The API answers with a paginated envelope ({items, total, ...}) whose
    // rows name the component `type`; hand the panel a plain list.
    const response: unknown = await apiClient.get(`/v2/device-versions?device_id=${encodeURIComponent(deviceId)}`);
    const items: Array<Record<string, unknown>> = Array.isArray(response)
      ? response
      : Array.isArray((response as { items?: unknown })?.items)
        ? ((response as { items: Array<Record<string, unknown>> }).items)
        : [];
    return items.map((row) => ({
      ...row,
      component_type: String(row.component_type ?? row.type ?? row.component ?? ''),
    })) as DeviceFirmwareVersion[];
  }
}

export const deviceService = new DeviceService();
